/**
 * Prix cités par les pages événement (Festival de Cannes…), lus dans
 * `VEHICLES` (app/config/infos.ts). Aucun montant n'est écrit à la main dans
 * une page : un tarif qui change met le titre, la description et la FAQ à
 * jour au build suivant.
 *
 * Un forfait absent fait ÉCHOUER le build plutôt que d'afficher un prix
 * fantôme (règle « rien d'inventé »).
 */
import { VEHICLES, getRouteKey, type VehicleId } from "@/config/infos";

const disposal = (id: VehicleId, hours: number): number => {
  const pkg = VEHICLES[id].disposalPackages.find((p) => p.hours === hours);
  if (!pkg) throw new Error(`eventPricing: pas de forfait ${hours} h en ${id}`);
  return pkg.price;
};

const transfer = (id: VehicleId, from: string, to: string): number => {
  const price = VEHICLES[id].fixedTransfers[getRouteKey(from, to)];
  if (typeof price !== "number")
    throw new Error(`eventPricing: pas de forfait ${from}-${to} en ${id}`);
  return price;
};

const extraHours = (["CLASS_E", "CLASS_V", "CLASS_S"] as const).flatMap((id) =>
  VEHICLES[id].disposalPackages.map((p) => p.extraHour),
);

/** Mise à disposition et transferts vers Cannes et Monaco, Classes E/V/S. */
export const EVENT_PRICES = {
  e3: disposal("CLASS_E", 3),
  v3: disposal("CLASS_V", 3),
  s3: disposal("CLASS_S", 3),
  e8: disposal("CLASS_E", 8),
  v8: disposal("CLASS_V", 8),
  s8: disposal("CLASS_S", 8),
  extraMin: Math.min(...extraHours),
  extraMax: Math.max(...extraHours),
  niceE: transfer("CLASS_E", "CANNES", "NCE_AIRPORT"),
  niceV: transfer("CLASS_V", "CANNES", "NCE_AIRPORT"),
  niceS: transfer("CLASS_S", "CANNES", "NCE_AIRPORT"),
  monacoNiceE: transfer("CLASS_E", "MONACO", "NCE_AIRPORT"),
  monacoNiceV: transfer("CLASS_V", "MONACO", "NCE_AIRPORT"),
  monacoNiceS: transfer("CLASS_S", "MONACO", "NCE_AIRPORT"),
  cannesMonacoE: transfer("CLASS_E", "CANNES", "MONACO"),
  cannesMonacoV: transfer("CLASS_V", "CANNES", "MONACO"),
  cannesMonacoS: transfer("CLASS_S", "CANNES", "MONACO"),
} as const;
